// react
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// hooks
import { useCareProfile } from 'hooks';
import { useGoBack } from 'hooks/useGoBack';

// store
import { useSelector, dispatch } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';

// components
import { FormIntroPage } from '@karehero/llama/pwa/pages/FormIntroPage/FormIntroPage';
import { CareProfileCategoryID } from '@karehero/models';

/**
 * BenefitsEligibilityStart is the page displayed for starting the benefits eligibility process.
 */
export const BenefitsEligibilityStart = () => {
  // hooks
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { handleStart, toFirstFormPage, forms, answers, careProfileID } =
    useCareProfile({
      categoryID: CareProfileCategoryID.EligibilityCalculator,
    });
  const account = useSelector(getAccount);
  const isAdminWithoutCareProfile =
    account?.accountRole?.name === 'KareHero Administrator' && !careProfileID;

  const onStart = useCallback(() => {
    if (isAdminWithoutCareProfile) {
      dispatch(
        newToast({
          title: 'Please select a care circle first',
          variant: 'warning',
          action: {
            label: 'Select care circle',
            href: ROUTES.ADMIN_CARE_CIRCLE_MANAGE,
          },
        }),
      );
      return;
    }
    handleStart();
  }, [isAdminWithoutCareProfile, handleStart]);

  const isFormComplete =
    !!forms &&
    forms.length > 0 &&
    forms.every((f) =>
      Object.keys(f.fields).every(
        (fieldId) =>
          answers[fieldId] !== undefined &&
          answers[fieldId] !== null &&
          answers[fieldId] !== '',
      ),
    );

  return (
    <FormIntroPage
      onBack={goBack}
      title='Benefits Eligibility Calculator'
      description='Answer a few questions about your circumstances to find out which government benefits and funding you may be eligible for.'
      items={[
        {
          icon: 'check-circle',
          text: 'Answer questions about your circumstances to check eligibility for benefits.',
        },
        {
          icon: 'check-circle',
          text: 'Get instant results showing which benefits you may be eligible for.',
        },
        {
          icon: 'file-contract',
          text: 'Receive guidance on how to apply for the benefits you qualify for.',
        },
      ]}
      infoText="We've helped thousands of families discover benefits they didn't realise they were eligible for."
      additionalActions={
        isFormComplete
          ? [
              {
                label: 'View results',
                onClick: () =>
                  navigate(ROUTES.ELIGIBILITY_CALCULATOR_COMPLETE, {
                    replace: true,
                  }),
              },
            ]
          : undefined
      }
      onStart={onStart}
    />
  );
};

BenefitsEligibilityStart.displayName = 'BenefitsEligibilityStart';
export default BenefitsEligibilityStart;
